/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from './supabase';
import { StatusPill, Toast, useToastAutoDismiss } from './components/systemKit';
import RecipeDrawer from './components/RecipeDrawer';

// Menu Profitability — upload a menu (PDF/image), Claude parses dishes + estimated
// ingredients, then plate cost auto-tracks against current Price Tracker prices.
// All Claude calls go through /api/parse-invoice (the existing generic Anthropic proxy).
//
// Recipe drawer (linkage management) lives in src/components/RecipeDrawer.js — it
// replaced the legacy in-file <EditItemModal> when the Recipe Health surface
// shipped. The state machine + audit log + pricing_health derivation live in
// src/lib/recipeHealth.js.

const CATEGORIES = ['Entrees', 'Appetizers', 'Sides', 'Desserts', 'Drinks', 'Other'];
const MODEL = 'claude-sonnet-4-5';

// Convert qty between recipe unit and food_items unit. Only handles common pairs;
// anything else returns null and the ingredient stays "manual cost needed".
function convertQty(qty, fromUnit, toUnit) {
  if (!fromUnit || !toUnit) return qty;
  const f = fromUnit.toLowerCase().trim();
  const t = toUnit.toLowerCase().trim();
  if (f === t) return qty;
  if (f === 'oz' && t === 'lb') return qty / 16;
  if (f === 'lb' && t === 'oz') return qty * 16;
  if (f === 'cup' && t === 'gal') return qty / 16;
  if (f === 'gal' && t === 'cup') return qty * 16;
  if (f === 'cup' && t === 'qt') return qty / 4;
  if (f === 'qt' && t === 'cup') return qty * 4;
  if (f === 'tbsp' && t === 'cup') return qty / 16;
  if (f === 'tsp' && t === 'cup') return qty / 48;
  return null;
}

function isPdfFile(f) {
  return f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name || ''));
}

// Best-match between an ingredient name and a food_items row. Used both during initial
// menu parse and on every page load so as new food_items appear (via Price Tracker invoices)
// previously-unlinked ingredients latch on automatically. Match strategy:
//   1. Exact normalized match — wins instantly.
//   2. Whole-word substring (ingredient appears as a complete word in food_item, or vice
//      versa) — high confidence. Avoids "fries" matching "fried chicken".
//   3. Token overlap fallback — only used when ingredient is short (≤3 tokens) and at
//      least half its tokens appear in the food_item name. Catches "Chicken Breast" →
//      "Chicken Breast 8oz Frozen" without false-matching one-word generics.
const STOP_WORDS = new Set(['salt', 'pepper', 'oil', 'water', 'fresh', 'dried']);
function findBestFoodItemMatch(ingredientName, foodItems) {
  const norm = (s) => (s || '').toLowerCase().replace(/[(),.\-/]/g, ' ').replace(/\s+/g, ' ').trim();
  const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const ingNorm = norm(ingredientName);
  if (!ingNorm) return null;
  const ingTokens = ingNorm.split(' ').filter(t => t.length >= 3 && !STOP_WORDS.has(t));
  if (ingTokens.length === 0) return null;

  let best = null;
  let bestScore = 0;
  for (const fi of foodItems) {
    const fiNorm = norm(fi.normalized_name);
    if (!fiNorm) continue;
    if (fiNorm === ingNorm) return fi;
    let score = 0;
    const ingAsWord = new RegExp('\\b' + escapeRe(ingNorm) + '\\b');
    const fiAsWord = new RegExp('\\b' + escapeRe(fiNorm) + '\\b');
    if (ingAsWord.test(fiNorm) || fiAsWord.test(ingNorm)) score = 100;
    else {
      const fiTokens = new Set(fiNorm.split(' ').filter(t => t.length >= 3 && !STOP_WORDS.has(t)));
      const overlap = ingTokens.filter(t => fiTokens.has(t)).length;
      if (overlap >= 1 && ingTokens.length <= 3 && overlap / ingTokens.length >= 0.5) {
        score = overlap * 10;
      }
    }
    if (score > bestScore) { best = fi; bestScore = score; }
  }
  return bestScore > 0 ? best : null;
}

// Robust JSON parser for Claude's menu output. Multi-screenshot menus can blow past
// max_tokens and leave the response truncated mid-string. Strategy:
//   1. Try a clean JSON.parse first.
//   2. On failure, walk the text from the start tracking brace depth + string state and
//      stop at the LAST point where we successfully closed an object at array depth 1.
//      Discard the partial trailing object, append a closing ']', and parse what we recovered.
// Returns the parsed array, or throws if even recovery fails.
function parseMenuJson(text) {
  try { return JSON.parse(text); } catch (_) { /* fall through */ }

  // Find the start of the array.
  const start = text.indexOf('[');
  if (start < 0) throw new Error('Menu parsing returned no items.');

  let depth = 0;        // 0 = before/after array, 1 = inside array, 2+ = inside an object
  let inString = false;
  let escape = false;
  let lastSafeEnd = -1; // index just after a closed top-level object inside the array

  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escape) { escape = false; continue; }
      if (ch === '\\') { escape = true; continue; }
      if (ch === '"') { inString = false; }
      continue;
    }
    if (ch === '"') { inString = true; continue; }
    if (ch === '[' || ch === '{') { depth++; continue; }
    if (ch === ']' || ch === '}') {
      depth--;
      // Just closed an object that lived directly inside the array.
      if (ch === '}' && depth === 1) lastSafeEnd = i + 1;
      // Closed the outer array cleanly — try the whole thing.
      if (ch === ']' && depth === 0) {
        try { return JSON.parse(text.slice(start, i + 1)); } catch (_) { /* keep trying */ }
      }
    }
  }

  if (lastSafeEnd < 0) throw new Error('Menu parsing returned malformed output.');
  // Drop a trailing comma if present, then close the array.
  let recovered = text.slice(start, lastSafeEnd).replace(/,\s*$/, '') + ']';
  try {
    return JSON.parse(recovered);
  } catch (e) {
    throw new Error('Menu was too large to fully parse. Try splitting into fewer screenshots.');
  }
}

function fmtMoney(n) {
  if (n == null || isNaN(n)) return '—';
  return `$${Number(n).toFixed(2)}`;
}
function fmtFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
function fmtPct(n) {
  if (n == null || !isFinite(n)) return '—';
  return `${Number(n).toFixed(1)}%`;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function MenuProfitability({ companyId, navConfig, onConsumeNavConfig }) {
  const [items, setItems] = useState([]);             // menu_items + ingredients joined
  const [foodItems, setFoodItems] = useState([]);     // for ingredient ↔ food_items linking
  const [settings, setSettings] = useState({ target_margin_pct: 70, alert_threshold_pct: 5 });
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // menu_item being edited (null = closed)
  // When the operator clicks a pricing_health chip on a card, drawer opens
  // pre-scrolled to the first pending_review ingredient. One-shot — cleared
  // when drawer closes or opened via a non-chip path.
  const [drawerScrollFirstPending, setDrawerScrollFirstPending] = useState(false);
  // Mutation feedback toast — drawer's state-machine actions surface success
  // / failure here. systemKit.Toast handles the render + auto-dismiss.
  const [toast, setToast] = useState(null);
  useToastAutoDismiss(toast, setToast);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('All');
  // Affected-food-item filter: when the operator clicks "View affected
  // dishes" from the InvoiceReviewModal, we narrow the card grid to only
  // dishes that contain at least one of the food_items that moved on
  // that invoice. Set from navConfig on mount; cleared via the "Clear"
  // button in the banner. See lib/navigate.js #navigate('menu', ...).
  const [affectedFoodItemIds, setAffectedFoodItemIds] = useState(null);
  const [affectedSourceVendor, setAffectedSourceVendor] = useState(null);

  useEffect(() => {
    if (!navConfig) return;
    if (Array.isArray(navConfig.affectedFoodItemIds) && navConfig.affectedFoodItemIds.length > 0) {
      setAffectedFoodItemIds(new Set(navConfig.affectedFoodItemIds.map(Number)));
      setAffectedSourceVendor(navConfig.sourceVendor || null);
    }
    onConsumeNavConfig?.();
  }, [navConfig, onConsumeNavConfig]);

  useEffect(() => { if (companyId) fetchAll(); }, [companyId]);

  async function fetchAll() {
    setLoading(true);
    const [itemsRes, ingRes, foodRes, setRes] = await Promise.all([
      supabase.from('menu_items').select('*').eq('company_id', companyId).eq('is_active', true).order('category').order('name'),
      supabase.from('recipe_ingredients').select('*').eq('company_id', companyId),
      supabase.from('food_items').select('id, normalized_name, current_price, price_per_lb, weight_per_case, unit, price_change_pct').eq('company_id', companyId),
      supabase.from('menu_settings').select('*').eq('company_id', companyId).maybeSingle(),
    ]);
    if (setRes.data) setSettings(setRes.data);
    const food = foodRes.data || [];
    setFoodItems(food);

    // Auto-relink unlinked ingredients on every load. As Price Tracker invoices add new
    // food_items rows over time, previously-unlinked ingredients (e.g. "fries") latch
    // onto them automatically — so a price change on the master row propagates to every
    // dish using that ingredient without the manager having to re-edit each recipe.
    const allIngs = ingRes.data || [];
    allIngs.forEach(ing => {
      if (ing.food_item_id) return;
      const match = findBestFoodItemMatch(ing.ingredient_name, food);
      if (match) ing.food_item_id = match.id;
    });

    // Recompute plate costs from latest food_items prices in case invoices have come in since
    // the dish was created. Linked ingredients live-update; one master price drives every dish.
    const ingByItem = {};
    allIngs.forEach(ing => {
      if (!ingByItem[ing.menu_item_id]) ingByItem[ing.menu_item_id] = [];
      ingByItem[ing.menu_item_id].push(refreshIngredientCost(ing, food));
    });
    const enriched = (itemsRes.data || []).map(it => {
      const ings = ingByItem[it.id] || [];
      const plateCost = ings.reduce((s, ing) => s + (Number(ing.line_cost) || 0), 0);
      const marginPct = it.menu_price > 0 ? ((it.menu_price - plateCost) / it.menu_price) * 100 : null;
      return { ...it, ingredients: ings, plate_cost: plateCost, margin_pct: marginPct };
    });
    setItems(enriched);
    setLoading(false);

    // Persist drift back so the editor + cards always agree and stale wrong values get fixed.
    // Two sets of writes happen here:
    //   1. menu_items.plate_cost / margin_pct when they shifted.
    //   2. recipe_ingredients.unit_cost / line_cost / food_item_id where the live recompute or
    //      auto-relink produced different values than what's stored. This corrects the
    //      historical bug where unit_cost was stored as the food_item's per-its-own-unit cost
    //      (e.g. $1.64/lb) instead of per-recipe-unit ($0.10/oz), which made the editor show
    //      inflated line costs.
    enriched.forEach(it => {
      const original = itemsRes.data.find(r => r.id === it.id);
      if (Math.abs((Number(it.plate_cost) || 0) - (Number(original?.plate_cost) || 0)) > 0.01) {
        supabase.from('menu_items').update({
          plate_cost: it.plate_cost,
          margin_pct: it.margin_pct,
          updated_at: new Date().toISOString(),
        }).eq('id', it.id).then(() => {});
      }
    });
    const ingFixUpdates = [];
    enriched.forEach(it => {
      it.ingredients.forEach(ing => {
        if (!ing.id) return;
        const original = (ingRes.data || []).find(r => r.id === ing.id);
        if (!original) return;
        const newU = Number(ing.unit_cost || 0);
        const newL = Number(ing.line_cost || 0);
        const oldU = Number(original.unit_cost || 0);
        const oldL = Number(original.line_cost || 0);
        const newFi = ing.food_item_id || null;
        const oldFi = original.food_item_id || null;
        if (Math.abs(newU - oldU) > 0.0001 || Math.abs(newL - oldL) > 0.0001 || newFi !== oldFi) {
          ingFixUpdates.push({ id: ing.id, unit_cost: newU, line_cost: newL, food_item_id: newFi });
        }
      });
    });
    if (ingFixUpdates.length > 0) {
      Promise.all(ingFixUpdates.map(u =>
        supabase.from('recipe_ingredients').update({
          unit_cost: u.unit_cost,
          line_cost: u.line_cost,
          food_item_id: u.food_item_id,
        }).eq('id', u.id)
      )).catch(() => {});
    }
  }

  // Pull live cost for a linked ingredient from food_items, with unit conversion.
  // Returns unit_cost in the RECIPE'S unit (per oz, per each, etc.) not the food_item's
  // unit. Critical: the edit modal multiplies stored unit_cost × quantity directly, so
  // unit_cost has to already be in recipe units or the editor math is wrong.
  function refreshIngredientCost(ing, food) {
    const qty = Number(ing.quantity || 0);
    const storedUnitCost = Number(ing.unit_cost || 0);
    if (!ing.food_item_id) return { ...ing, unit_cost: storedUnitCost, line_cost: storedUnitCost * qty };
    const fi = food.find(f => f.id === ing.food_item_id);
    if (!fi) return { ...ing, unit_cost: storedUnitCost, line_cost: storedUnitCost * qty };
    // Pick the food_item price + unit. Prefer per_lb when the food unit is "case" since case
    // prices fluctuate with pack weight; per_lb is the more stable cost driver.
    let costPerFoodUnit = Number(fi.current_price);
    let foodUnit = fi.unit || 'each';
    if (fi.price_per_lb != null && (foodUnit.toLowerCase() === 'case' || foodUnit.toLowerCase() === 'cs')) {
      costPerFoodUnit = Number(fi.price_per_lb);
      foodUnit = 'lb';
    }
    // How many food units make up 1 recipe unit? E.g. 1 oz = 0.0625 lb.
    const convFactor = convertQty(1, ing.unit, foodUnit);
    if (convFactor == null) {
      // Units don't convert — leave the manually-stored unit_cost in place.
      return { ...ing, unit_cost: storedUnitCost, line_cost: storedUnitCost * qty };
    }
    const perRecipeUnitCost = costPerFoodUnit * convFactor; // e.g. $1.64/lb × 0.0625 = $0.1025/oz
    return { ...ing, unit_cost: perRecipeUnitCost, line_cost: perRecipeUnitCost * qty };
  }

  // ── KPIs ──
  const kpis = useMemo(() => {
    const tracked = items.length;
    const categories = new Set(items.map(i => i.category).filter(Boolean)).size;
    const withMargin = items.filter(i => i.margin_pct != null);
    const avgMargin = withMargin.length > 0
      ? withMargin.reduce((s, i) => s + Number(i.margin_pct), 0) / withMargin.length
      : null;
    const target = Number(settings.target_margin_pct) || 70;
    const belowTarget = items.filter(i => i.margin_pct != null && Number(i.margin_pct) < target).length;
    const alertThreshold = Number(settings.alert_threshold_pct) || 5;
    const alerted = items.filter(i =>
      (i.ingredients || []).some(ing => {
        const fi = foodItems.find(f => f.id === ing.food_item_id);
        return fi && Math.abs(Number(fi.price_change_pct || 0)) > alertThreshold;
      })
    ).length;
    return { tracked, categories, avgMargin, belowTarget, alerted, target };
  }, [items, foodItems, settings]);

  // Group menu items by category. The categoryFilter pill above narrows what's shown;
  // the section grouping itself stays so users can still see categories at a glance.
  const itemsByCategory = useMemo(() => {
    const map = {};
    items.forEach(i => {
      const cat = i.category || 'Other';
      if (categoryFilter !== 'All' && cat !== categoryFilter) return;
      // Affected-items filter — narrow to dishes that contain at least
      // one of the moved food_items.
      if (affectedFoodItemIds && affectedFoodItemIds.size > 0) {
        const hit = (i.ingredients || []).some(ing => ing.food_item_id && affectedFoodItemIds.has(Number(ing.food_item_id)));
        if (!hit) return;
      }
      if (!map[cat]) map[cat] = [];
      map[cat].push(i);
    });
    return CATEGORIES.filter(c => map[c]).map(c => ({ category: c, items: map[c] }))
      .concat(Object.keys(map).filter(c => !CATEGORIES.includes(c)).map(c => ({ category: c, items: map[c] })));
  }, [items, categoryFilter, affectedFoodItemIds]);

  // Categories actually present in the menu — drives which chips render.
  const presentCategories = useMemo(() => {
    const seen = new Set(items.map(i => i.category || 'Other'));
    return CATEGORIES.filter(c => seen.has(c)).concat([...seen].filter(c => !CATEGORIES.includes(c)));
  }, [items]);

  // Margin classification — green ≥ target, amber within 5pp, red below target-5.
  function marginTier(pct) {
    const target = Number(settings.target_margin_pct) || 70;
    if (pct == null) return 'unknown';
    if (pct >= target) return 'good';
    if (pct >= target - 5) return 'ok';
    return 'low';
  }

  async function generateSuggestions() {
    if (items.length === 0) return;
    setSuggestionsLoading(true);
    const target = Number(settings.target_margin_pct) || 70;
    const dataset = items.map(i => ({
      dish: i.name,
      category: i.category,
      menu_price: Number(i.menu_price),
      plate_cost: Number(i.plate_cost || 0),
      margin_pct: Number(i.margin_pct || 0),
      ingredients: (i.ingredients || []).map(ing => {
        const fi = foodItems.find(f => f.id === ing.food_item_id);
        return {
          name: ing.ingredient_name,
          qty: Number(ing.quantity), unit: ing.unit,
          line_cost: Number(ing.line_cost || 0),
          recent_price_change_pct: fi?.price_change_pct ? Number(fi.price_change_pct) : null,
        };
      }),
    }));
    try {
      const sys = `You are a food cost advisor for an independent restaurant. Analyze the following menu items, their ingredient costs, current margins, and recent price changes. Provide specific, actionable recommendations.

For each item that needs attention, provide ONE of these recommendation types:
1. PRICE INCREASE: "Raise [dish] from $X to $Y to restore a Z% margin" — calculate the exact new price
2. PORTION ADJUSTMENT: "Reduce [ingredient] from Xoz to Yoz to save $Z per plate"
3. SUBSTITUTION: "Replace [expensive ingredient] with [cheaper alternative] to save $Z per plate"
4. PROMOTION: "This item has a strong Z% margin — consider featuring it to drive volume"

Rules:
- Be specific with dollar amounts and percentages — no vague advice
- Only flag items where the margin is below the target (${target}%) or where a price increase opportunity exists
- For price increase suggestions, round to the nearest $0.50 or $1.00 (menus use round numbers)
- Include at most 5 suggestions, ranked by financial impact
- Reference actual ingredient names and price changes from the data provided

Format each suggestion as a JSON object:
{ "dish": "name", "type": "price_increase|portion|substitution|promotion", "summary": "one sentence", "detail": "full recommendation text", "impact": "estimated dollar savings or margin improvement" }

Return ONLY a JSON array of suggestions, no markdown.`;
      const res = await fetch('/api/parse-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: MODEL,
          max_tokens: 2048,
          system: sys,
          messages: [{ role: 'user', content: JSON.stringify({ target_margin_pct: target, items: dataset }) }],
          _meta: { feature: 'menu-suggestions', companyId },
        }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || '[]';
      // Strip stray code-fences in case the model adds them despite the prompt.
      const cleaned = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsed = JSON.parse(cleaned);
      setSuggestions(Array.isArray(parsed) ? parsed : []);
    } catch (err) {
      // Fail soft — leave suggestions panel empty.
      setSuggestions([]);
    }
    setSuggestionsLoading(false);
  }

  // Auto-fire suggestions when data loads & there's actually something to suggest.
  useEffect(() => {
    if (!loading && items.length > 0 && suggestions.length === 0) {
      generateSuggestions();
    }
  }, [loading, items.length]);

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 md:mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Menu profitability</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Plate cost &amp; margin per dish, auto-tracking your invoice prices.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditingItem({ id: null, name: '', category: 'Entrees', menu_price: '', ingredients: [] })}
              className="text-sm font-medium rounded-lg transition-colors"
              style={{ background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)', padding: '8px 14px', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              + Add item
            </button>
            <button
              onClick={() => setShowUpload(true)}
              className="text-sm font-medium rounded-lg transition-colors"
              style={{ background: 'var(--text-primary)', color: 'var(--bg-card)', border: 'none', padding: '8px 14px', cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#262626')}
              onMouseLeave={e => (e.currentTarget.style.background = 'var(--text-primary)')}
            >
              Upload menu
            </button>
          </div>
        </div>

        {/* Affected-dishes filter banner. Only renders when navigated in
            from the InvoiceReviewModal "View affected dishes" CTA. Shows
            the count of matched dishes and a Clear control to escape the
            filter and return to the full menu. */}
        {affectedFoodItemIds && affectedFoodItemIds.size > 0 && (
          <div
            className="mb-5 px-4 py-3 rounded-lg flex items-center gap-3"
            style={{
              background: 'color-mix(in srgb, var(--text-warning) 8%, transparent)',
              border: '1px solid color-mix(in srgb, var(--text-warning) 30%, transparent)',
            }}
          >
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--text-warning)',
              padding: '2px 6px', borderRadius: 4,
              background: 'color-mix(in srgb, var(--text-warning) 12%, transparent)',
            }}>Filter</span>
            <div style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)' }}>
              Dishes affected by {affectedSourceVendor ? `${affectedSourceVendor.replace(/\n/g, ' / ')} ` : ''}invoice price changes
              {' · '}
              <span style={{ color: 'var(--text-secondary)' }}>
                {itemsByCategory.reduce((s, g) => s + g.items.length, 0)} match{itemsByCategory.reduce((s, g) => s + g.items.length, 0) === 1 ? '' : 'es'}
              </span>
            </div>
            <button
              onClick={() => { setAffectedFoodItemIds(null); setAffectedSourceVendor(null); }}
              style={{
                background: 'transparent', border: '1px solid var(--border)',
                borderRadius: 6, fontSize: 12, padding: '4px 10px',
                color: 'var(--text-secondary)', cursor: 'pointer',
              }}
            >Clear filter</button>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-5 md:mb-6">
          <KpiCard label="Menu items" value={kpis.tracked} subtitle={kpis.categories > 0 ? `across ${kpis.categories} categor${kpis.categories === 1 ? 'y' : 'ies'}` : 'no items yet'} />
          <KpiCard label="Avg margin" value={kpis.avgMargin != null ? fmtPct(kpis.avgMargin) : '—'} subtitle={`target: ${Number(settings.target_margin_pct).toFixed(0)}%`} valueClass={kpis.avgMargin != null && kpis.avgMargin >= Number(settings.target_margin_pct) ? 'text-green-600' : kpis.avgMargin != null ? 'text-amber-600' : ''} />
          <KpiCard label="Below target" value={kpis.belowTarget} subtitle={kpis.belowTarget > 0 ? 'need attention' : 'all on target'} valueClass={kpis.belowTarget > 0 ? 'text-red-600' : 'text-green-600'} />
          <KpiCard label="Price alerts" value={kpis.alerted} subtitle={kpis.alerted > 0 ? 'ingredient costs changed' : 'no movement'} valueClass={kpis.alerted > 0 ? 'text-amber-600' : ''} />
        </div>

        {/* Body */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {[0, 1, 2, 3].map(i => <div key={i} className="h-44 rounded-2xl bg-white border border-gray-200 animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState onUpload={() => setShowUpload(true)} onAdd={() => setEditingItem({ id: null, name: '', category: 'Entrees', menu_price: '', ingredients: [] })} />
        ) : (
          <>
            {/* Category filter chips — focus the grid on one category at a time. Only renders
               categories that actually have items so the row doesn't show empty options. */}
            {presentCategories.length > 1 && (
              <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
                {['All', ...presentCategories].map(c => (
                  <button
                    key={c}
                    onClick={() => setCategoryFilter(c)}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-colors border ${
                      categoryFilter === c
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {c}{c !== 'All' && <span className="ml-1.5 opacity-60">{items.filter(i => (i.category || 'Other') === c).length}</span>}
                  </button>
                ))}
              </div>
            )}
            {itemsByCategory.map(({ category, items: catItems }) => (
              <div key={category} className="mb-6">
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">{category}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  {catItems.map(item => (
                    <MenuItemCard
                      key={item.id}
                      item={item}
                      tier={marginTier(item.margin_pct)}
                      target={Number(settings.target_margin_pct) || 70}
                      threshold={Number(settings.alert_threshold_pct) || 5}
                      foodItems={foodItems}
                      onClick={() => { setEditingItem(item); setDrawerScrollFirstPending(false); }}
                      onChipClick={() => { setEditingItem(item); setDrawerScrollFirstPending(true); }}
                    />
                  ))}
                </div>
              </div>
            ))}

            {/* AI Suggestions */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 mt-2">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-500"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                  Price impact suggestions
                </h2>
                <button onClick={generateSuggestions} disabled={suggestionsLoading} className="text-xs text-blue-600 hover:underline disabled:opacity-50">
                  {suggestionsLoading ? 'Refreshing…' : 'Refresh suggestions'}
                </button>
              </div>
              {suggestionsLoading && suggestions.length === 0 ? (
                <div className="py-6 text-center text-sm text-gray-400">Analyzing your menu…</div>
              ) : suggestions.length === 0 ? (
                <div className="py-6 text-center text-sm text-gray-400">No suggestions right now. All items are tracking on target.</div>
              ) : (
                <div className="space-y-2">
                  {suggestions.map((s, i) => (
                    <SuggestionRow key={i} s={s} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {showUpload && (
        <UploadMenuModal
          companyId={companyId}
          foodItems={foodItems}
          onClose={() => setShowUpload(false)}
          onComplete={() => { setShowUpload(false); fetchAll(); }}
        />
      )}
      {/* Recipe drawer (slide-over) — replaces the legacy <EditItemModal>.
          Hosts linkage management state machine + margin attribution +
          existing edit fields. See src/components/RecipeDrawer.js. */}
      <RecipeDrawer
        open={editingItem != null}
        onClose={() => { setEditingItem(null); setDrawerScrollFirstPending(false); }}
        companyId={companyId}
        item={editingItem}
        foodItems={foodItems}
        onSaved={() => fetchAll()}
        scrollToFirstPending={drawerScrollFirstPending}
        onToast={setToast}
      />

      {/* Mutation feedback toast (linkage state-machine transitions) */}
      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

// ── Sub-components ──

// Local KpiCard — per DESIGN_SYSTEM.md §Cards. valueClass supports the legacy
// text-green-600 / text-amber-600 / text-red-600 markers passed from callers;
// we map them to CSS-var functional colors so dark mode adapts.
function KpiCard({ label, value, subtitle, valueClass }) {
  const valueColor =
    valueClass?.includes('text-green-600')  ? 'var(--text-success)'  :
    valueClass?.includes('text-amber-600')  ? 'var(--text-warning)'  :
    valueClass?.includes('text-red-600')    ? 'var(--text-critical)' :
    'var(--text-primary)';
  return (
    <div className="rounded-lg p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1, color: valueColor }}>{value}</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>{subtitle}</div>
    </div>
  );
}

function EmptyState({ onUpload, onAdd }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
      <svg className="mx-auto mb-4 text-gray-300" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
      </svg>
      <h3 className="text-base font-medium text-gray-900 mb-1">No menu items yet</h3>
      <p className="text-sm text-gray-500 mb-5">Upload your menu to start tracking profitability — or add a single dish manually.</p>
      <div className="flex items-center justify-center gap-2">
        <button onClick={onUpload} className="text-sm px-4 py-2 rounded-lg text-white font-medium" style={{ background: 'var(--text-primary)', color: 'var(--bg-card)' }}>Upload menu</button>
        <button onClick={onAdd} className="text-sm px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 font-medium hover:bg-gray-50">Add manually</button>
      </div>
    </div>
  );
}

function MenuItemCard({ item, tier, target, threshold, foodItems, onClick, onChipClick }) {
  // Identify the worst price-change ingredient for the alert pill.
  const alertIng = (() => {
    const candidates = (item.ingredients || []).map(ing => {
      const fi = foodItems.find(f => f.id === ing.food_item_id);
      const pct = fi ? Number(fi.price_change_pct || 0) : 0;
      return { name: ing.ingredient_name, pct };
    }).filter(c => Math.abs(c.pct) > threshold);
    candidates.sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct));
    return candidates[0] || null;
  })();

  // Pricing-health chip — surfaces ingredient linkage state at the card
  // level. Click bypasses normal card click and opens drawer scrolled to
  // the first pending_review ingredient. See DESIGN_SYSTEM.md
  // §Recipe Health surface.
  const pendingCount = (item.ingredients || []).filter(i => i.link_status === 'pending_review').length;
  const unlinkedCount = (item.ingredients || []).filter(i => i.link_status === 'unlinked_no_cost').length;
  const manualCount = (item.ingredients || []).filter(i => i.link_status === 'unlinked_manual_cost').length;
  const healthChip = (() => {
    switch (item.pricing_health) {
      case 'has_pending_review': return { tone: 'amber', label: `${pendingCount} to review`, important: true };
      case 'has_unlinked':       return { tone: 'amber', label: `${unlinkedCount} unlinked`, important: true };
      case 'incomplete':         return { tone: 'amber', label: 'Incomplete', important: true };
      case 'no_recipe':          return { tone: 'neutral', label: 'No recipe', important: false };
      case 'healthy':            return manualCount > 0
                                   ? { tone: 'neutral', label: `${manualCount} manual`, important: false }
                                   : { tone: 'green', label: 'Healthy', important: false };
      default:                   return null;
    }
  })();

  const ringStyle = {
    good: { background: '#DCFCE7', color: '#166534' },
    ok: { background: '#FEF3C7', color: '#92400E' },
    low: { background: '#FEE2E2', color: '#991B1B' },
    unknown: { background: '#F3F4F6', color: '#9CA3AF' },
  }[tier];

  const barColor = tier === 'good' ? '#2D8F5E' : tier === 'ok' ? '#D97706' : tier === 'low' ? '#DC2626' : '#9CA3AF';
  const costPct = item.menu_price > 0 ? Math.min(100, (Number(item.plate_cost) / Number(item.menu_price)) * 100) : 0;

  return (
    <button
      onClick={onClick}
      className="relative text-left bg-white rounded-2xl border border-gray-200 p-4 md:p-5 hover:border-gray-300 hover:shadow-sm transition-all"
    >
      {/* Margin ring */}
      <div
        className="absolute top-3 right-3 w-12 h-12 rounded-full flex items-center justify-center text-xs font-semibold"
        style={ringStyle}
      >
        {item.margin_pct != null ? `${Number(item.margin_pct).toFixed(0)}%` : '—'}
      </div>

      <div className="pr-14">
        <div className="text-sm md:text-base font-semibold text-gray-900 truncate">{item.name}</div>
        <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span>{item.category || 'Uncategorized'}</span>
          <span>·</span>
          <span>{fmtMoney(item.menu_price)}</span>
          {healthChip && (
            <span
              onClick={e => { e.stopPropagation(); onChipClick?.(); }}
              role="button" tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onChipClick?.(); } }}
              style={{ cursor: 'pointer' }}
              title="Click to review ingredient linkages"
            >
              <StatusPill tone={healthChip.tone}>{healthChip.label}</StatusPill>
            </span>
          )}
        </div>
      </div>

      {/* Ingredient list */}
      <div className="mt-3 space-y-1">
        {(item.ingredients || []).slice(0, 5).map(ing => {
          const linked = !!ing.food_item_id;
          return (
            <div key={ing.id || ing.ingredient_name} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${linked ? 'bg-green-500' : 'bg-gray-300'}`} title={linked ? 'Linked to Price Tracker' : 'Manual cost'} />
                <span className="text-gray-700 truncate">{ing.ingredient_name}</span>
                <span className="text-gray-400 flex-shrink-0">{Number(ing.quantity)}{ing.unit}</span>
              </div>
              <span className="text-gray-500 ml-2 flex-shrink-0 font-mono text-[11px]">{fmtMoney(ing.line_cost)}</span>
            </div>
          );
        })}
        {(item.ingredients || []).length === 0 && (
          <div className="text-xs text-gray-400 italic">Add ingredients to calculate margin</div>
        )}
        {(item.ingredients || []).length > 5 && (
          <div className="text-[11px] text-gray-400">+{item.ingredients.length - 5} more</div>
        )}
      </div>

      {/* Cost bar */}
      {item.menu_price > 0 && (item.ingredients || []).length > 0 && (
        <div className="mt-3">
          <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${costPct}%`, background: barColor }} />
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-3 flex items-center justify-between text-[11px]">
        <span className="text-gray-500">Plate cost: <span className="font-medium text-gray-900">{fmtMoney(item.plate_cost)}</span></span>
        {alertIng ? (
          <span className="px-2 py-0.5 rounded-full font-medium bg-red-50 text-red-700">
            {alertIng.name} {alertIng.pct > 0 ? '+' : ''}{alertIng.pct.toFixed(0)}%
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-400">No alerts</span>
        )}
      </div>
    </button>
  );
}

// Row in the queued-files list inside the upload modal. Shows a thumbnail for images,
// a doc icon for PDFs, and a remove button. Thumbnails use object URLs that are revoked
// on unmount to avoid leaking blob memory.
function FileRow({ file, onRemove }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (isPdfFile(file)) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {previewUrl ? (
          <img src={previewUrl} alt={file.name} className="w-full h-full object-cover" />
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h6"/></svg>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-gray-900 truncate" title={file.name}>{file.name}</div>
        <div className="text-[11px] text-gray-400">{fmtFileSize(file.size)}{isPdfFile(file) ? ' · PDF' : ''}</div>
      </div>
      <button onClick={onRemove} className="text-gray-400 hover:text-red-600 flex-shrink-0" title="Remove">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
      </button>
    </div>
  );
}

function SuggestionRow({ s }) {
  const isPromo = s.type === 'promotion';
  return (
    <div className={`px-3 py-2.5 border-l-[3px] rounded-r-md text-xs ${isPromo ? 'bg-blue-50 border-blue-500 text-blue-900' : 'bg-amber-50 border-amber-600 text-amber-900'}`}>
      <div className="font-semibold text-gray-900">{s.dish}</div>
      <div className="mt-1 text-gray-700">{s.detail || s.summary}</div>
      {s.impact && <div className="mt-1 text-[11px] text-gray-500">Impact: {s.impact}</div>}
    </div>
  );
}

// ── Upload + parse modal ──

function UploadMenuModal({ companyId, foodItems, onClose, onComplete }) {
  // Multi-file upload: paste / drop / pick can each contribute multiple images. PDF is allowed
  // as a single file (no point combining a PDF with screenshots — Claude can read multi-page PDFs).
  const [files, setFiles] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [step, setStep] = useState('');
  const [parsed, setParsed] = useState(null); // array of items
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const MAX_FILES = 10;
  const MAX_PER_FILE = 10 * 1024 * 1024;
  const MAX_TOTAL = 30 * 1024 * 1024;

  function addFiles(incoming) {
    const list = Array.from(incoming || []);
    if (list.length === 0) return;
    setError('');
    setFiles(prev => {
      const next = [...prev];
      for (const f of list) {
        if (!f) continue;
        if (f.size > MAX_PER_FILE) { setError(`"${f.name}" is too large (max 10 MB per file).`); continue; }
        // Reject PDF if mixing with images. Also reject second PDF.
        const hasPdf = next.some(x => isPdfFile(x));
        const incomingIsPdf = isPdfFile(f);
        if ((hasPdf && next.length > 0) || (incomingIsPdf && next.length > 0)) {
          setError('Upload either a single PDF OR up to 10 images — not both.');
          continue;
        }
        if (next.length >= MAX_FILES) { setError(`Max ${MAX_FILES} images per upload.`); break; }
        const totalNext = next.reduce((s, x) => s + x.size, 0) + f.size;
        if (totalNext > MAX_TOTAL) { setError('Total upload too large (max 30 MB combined).'); break; }
        next.push(f);
      }
      return next;
    });
  }

  function removeFile(idx) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }

  // Listen for clipboard paste while modal is open. Each pasted image becomes a screenshot.
  useEffect(() => {
    const handler = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const pasted = [];
      for (const it of items) {
        if (it.type && it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) {
            // Clipboard images come without a name; give them one so the row label reads cleanly.
            const named = new File([f], `screenshot-${Date.now()}-${pasted.length}.png`, { type: f.type });
            pasted.push(named);
          }
        }
      }
      if (pasted.length === 0) return;
      e.preventDefault();
      addFiles(pasted);
    };
    document.addEventListener('paste', handler);
    return () => document.removeEventListener('paste', handler);
  }, []);

  async function parseMenu() {
    if (files.length === 0) return;
    setParsing(true); setError(''); setParsed(null);
    try {
      setStep(files.length > 1 ? `Reading ${files.length} screenshots…` : 'Reading your menu…');
      // Build the message content. Single PDF gets a document block; images become a series of
      // image blocks (Claude treats them as one menu — each image labeled "Screenshot N of N").
      const isSinglePdf = files.length === 1 && isPdfFile(files[0]);
      const userBlocks = [];
      if (isSinglePdf) {
        const base64 = await fileToBase64(files[0]);
        userBlocks.push({
          type: 'document',
          source: { type: 'base64', media_type: 'application/pdf', data: base64 },
        });
      } else {
        for (let i = 0; i < files.length; i++) {
          const f = files[i];
          const base64 = await fileToBase64(f);
          if (files.length > 1) {
            userBlocks.push({ type: 'text', text: `Screenshot ${i + 1} of ${files.length}:` });
          }
          userBlocks.push({
            type: 'image',
            source: { type: 'base64', media_type: f.type || 'image/jpeg', data: base64 },
          });
        }
      }
      userBlocks.push({
        type: 'text',
        text: files.length > 1
          ? 'These are multiple screenshots of the same menu. Combine them into one JSON array of all distinct dishes. De-duplicate any item that appears in multiple screenshots.'
          : 'Parse this menu and return the JSON array.',
      });

      // Upload originals to storage for the audit trail (best-effort, don't block parsing).
      setStep('Uploading files…');
      const uploadStamp = Date.now();
      const uploadedPaths = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const path = `${companyId}/${uploadStamp}_${i}_${f.name}`;
        await supabase.storage.from('menus').upload(path, f).catch(() => {});
        uploadedPaths.push(path);
      }

      setStep('Identifying dishes…');
      const sys = `You are an expert at reading restaurant menus and estimating recipe compositions for food cost analysis. Parse this menu and return ONLY a JSON array — no markdown, no explanation, just raw JSON.

For each menu item, provide:
- name: string (the dish name as it appears on the menu)
- category: one of ["Entrees", "Appetizers", "Sides", "Desserts", "Drinks", "Other"]
- menu_price: number (the price on the menu, in dollars)
- ingredients: array of objects, each with:
  - name: string (common ingredient name, e.g. "Chicken breast", "Roma tomatoes")
  - quantity: number (estimated portion in the unit specified)
  - unit: string ("oz", "lb", "each", "cup", "tbsp", "tsp")
  - estimated_cost: number (your best estimate of cost for this portion at typical restaurant supplier prices)

Guidelines for ingredient estimation:
- Focus on the PRIMARY cost-driving ingredients (proteins, starches, expensive produce, dairy)
- Include 3-7 ingredients per dish — the ones that account for 80%+ of the plate cost
- Lump minor seasonings/garnishes into a single "Seasoning & garnish" line
- Use realistic restaurant-scale pricing (not retail grocery)
- For proteins: use boneless weight (what actually goes on the plate)
- Standard portion sizes: protein 6-8oz, starch side 5-6oz, salad 3-4oz, soup 8-10oz

If you receive multiple screenshots, treat them as different sections of one menu and combine into a single JSON array. De-duplicate any dish that appears more than once.

Return ONLY the JSON array.`;
      const res = await fetch('/api/parse-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: MODEL,
          // Bumped from 8K — multi-screenshot menus regularly produce 10K+ tokens of output
          // and the JSON would silently truncate, leaving the parser with an unterminated string.
          max_tokens: 32768,
          system: sys,
          messages: [{ role: 'user', content: userBlocks }],
          _meta: { feature: 'menu-parse', companyId, fileCount: files.length },
        }),
      });
      const data = await res.json();
      if (!res.ok || data?.type === 'error') throw new Error('Menu parsing is unavailable right now. Please try again.');
      const text = data.content?.[0]?.text || '[]';
      const cleaned = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const items = parseMenuJson(cleaned);
      if (!Array.isArray(items) || items.length === 0) throw new Error('No menu items found in these files.');

      setStep('Calculating costs…');
      // Best-effort linking against existing food_items by case-insensitive normalized_name.
      const enriched = items.map(it => ({
        ...it,
        category: CATEGORIES.includes(it.category) ? it.category : 'Other',
        ingredients: (it.ingredients || []).map(ing => {
          const fi = findBestFoodItemMatch(ing.name, foodItems);
          let unit_cost = Number(ing.estimated_cost) > 0 && Number(ing.quantity) > 0
            ? Number(ing.estimated_cost) / Number(ing.quantity)
            : 0;
          let food_item_id = null;
          if (fi) {
            food_item_id = fi.id;
            // If the recipe unit converts cleanly to the food item's unit, prefer the live price.
            const recipeUnit = ing.unit || 'each';
            let foodUnit = fi.unit || 'each';
            let costPerUnit = Number(fi.current_price) || 0;
            if (fi.price_per_lb != null && (foodUnit.toLowerCase() === 'case' || foodUnit.toLowerCase() === 'cs')) {
              costPerUnit = Number(fi.price_per_lb);
              foodUnit = 'lb';
            }
            const conv = convertQty(1, recipeUnit, foodUnit);
            if (conv != null) unit_cost = costPerUnit * conv;
          }
          return {
            ingredient_name: ing.name,
            quantity: Number(ing.quantity || 0),
            unit: ing.unit || 'each',
            unit_cost,
            line_cost: unit_cost * Number(ing.quantity || 0),
            food_item_id,
            is_ai_generated: true,
          };
        }),
      }));
      setParsed(enriched);
      setStep('');

      // Track the upload (one row per parse, file_url stores comma-joined paths when there's
      // more than one image — keeps the schema additive without a migration).
      await supabase.from('menu_uploads').insert({
        company_id: companyId,
        file_url: uploadedPaths.join(','),
        raw_claude_response: text.slice(0, 10000),
        items_parsed: enriched.length,
        status: 'parsed',
      });
    } catch (e) {
      setError(e.message); setStep('');
    }
    setParsing(false);
  }

  async function saveAll() {
    if (!parsed || parsed.length === 0) return;
    setSaving(true);
    try {
      for (const it of parsed) {
        // Upsert by (company_id, name) — if the dish already exists we replace its ingredients.
        const { data: itemRow, error: itemErr } = await supabase
          .from('menu_items')
          .upsert({
            company_id: companyId,
            name: it.name,
            category: it.category,
            menu_price: Number(it.menu_price) || 0,
            plate_cost: (it.ingredients || []).reduce((s, ing) => s + Number(ing.line_cost || 0), 0),
            margin_pct: it.menu_price > 0 ? (((Number(it.menu_price) - (it.ingredients || []).reduce((s, ing) => s + Number(ing.line_cost || 0), 0)) / Number(it.menu_price)) * 100) : null,
            is_active: true,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'company_id,name' })
          .select()
          .single();
        if (itemErr || !itemRow) continue;
        // Replace ingredients so re-uploading a menu is idempotent.
        await supabase.from('recipe_ingredients').delete().eq('menu_item_id', itemRow.id);
        if ((it.ingredients || []).length > 0) {
          await supabase.from('recipe_ingredients').insert(
            it.ingredients.map(ing => ({
              menu_item_id: itemRow.id,
              company_id: companyId,
              food_item_id: ing.food_item_id,
              ingredient_name: ing.ingredient_name,
              quantity: Number(ing.quantity || 0),
              unit: ing.unit || 'each',
              unit_cost: Number(ing.unit_cost || 0),
              line_cost: Number(ing.line_cost || 0),
              is_ai_generated: !!ing.is_ai_generated,
            }))
          );
        }
      }
      onComplete();
    } catch (e) {
      setError(`Save failed: ${e.message}`);
    }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={onClose}>
      <div className="bg-white w-full h-full md:h-auto md:max-w-3xl md:max-h-[90vh] md:rounded-2xl overflow-y-auto m-0 md:m-4" onClick={(e) => e.stopPropagation()} style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">{parsed ? 'Review parsed items' : 'Upload menu'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg" aria-label="Close">×</button>
        </div>

        <div className="p-5">
          {!parsed && (
            <>
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer?.files); }}
                className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-gray-400 transition-colors mb-4"
              >
                <svg className="mx-auto mb-3 text-gray-300" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>
                <p className="text-sm text-gray-600 font-medium">Drop menu files, paste screenshots, or click to browse</p>
                <p className="text-xs text-gray-400 mt-1">One PDF or up to 10 images (JPG, PNG, HEIC) · ⌘V / Ctrl+V to paste</p>
                <input ref={inputRef} type="file" multiple className="hidden" accept=".pdf,.jpg,.jpeg,.png,.heic" onChange={(e) => { addFiles(e.target.files); if (inputRef.current) inputRef.current.value = ''; }} />
              </div>

              {/* Queued files list — each row shows a thumbnail (or doc icon for PDF) + filename + remove. */}
              {files.length > 0 && (
                <div className="border border-gray-200 rounded-xl divide-y divide-gray-50 mb-4 max-h-72 overflow-y-auto">
                  {files.map((f, i) => (
                    <FileRow key={i} file={f} onRemove={() => removeFile(i)} />
                  ))}
                </div>
              )}

              {files.length > 0 && !parsing && (
                <button onClick={parseMenu} className="w-full py-2.5 text-sm font-medium text-white rounded-lg" style={{ background: 'var(--text-primary)', color: 'var(--bg-card)' }}>
                  Parse {files.length === 1 ? 'menu' : `${files.length} screenshots`}
                </button>
              )}
              {parsing && (
                <div className="flex items-center gap-3 py-3">
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
                  <span className="text-sm text-gray-600">{step}</span>
                </div>
              )}
              {error && (
                <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-700">{error}</div>
              )}
            </>
          )}

          {parsed && (
            <>
              <p className="text-xs text-gray-500 mb-3">{parsed.length} items parsed. Review the names and prices, then save. You can edit ingredients per dish after saving.</p>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="grid bg-gray-50 border-b border-gray-200 text-[11px] font-medium text-gray-500 uppercase tracking-wide" style={{ gridTemplateColumns: '2fr 1fr 80px 1fr' }}>
                  <div className="px-3 py-2">Name</div>
                  <div className="px-3 py-2">Category</div>
                  <div className="px-3 py-2 text-right">Price</div>
                  <div className="px-3 py-2 text-right">Plate / margin</div>
                </div>
                {parsed.map((it, idx) => {
                  const cost = (it.ingredients || []).reduce((s, ing) => s + Number(ing.line_cost || 0), 0);
                  const margin = it.menu_price > 0 ? (((Number(it.menu_price) - cost) / Number(it.menu_price)) * 100) : null;
                  return (
                    <div key={idx} className="grid border-b border-gray-50" style={{ gridTemplateColumns: '2fr 1fr 80px 1fr' }}>
                      <div className="px-3 py-2">
                        <input value={it.name} onChange={(e) => setParsed(prev => prev.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} className="w-full text-sm border-0 focus:outline-none focus:ring-0 bg-transparent" />
                        <div className="text-[11px] text-gray-400 mt-0.5">{(it.ingredients || []).length} ingredients</div>
                      </div>
                      <div className="px-3 py-2">
                        <select value={it.category} onChange={(e) => setParsed(prev => prev.map((x, i) => i === idx ? { ...x, category: e.target.value } : x))} className="w-full text-sm border-0 focus:outline-none bg-transparent">
                          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="px-3 py-2 text-right">
                        <input type="number" step="0.01" value={it.menu_price} onChange={(e) => setParsed(prev => prev.map((x, i) => i === idx ? { ...x, menu_price: e.target.value } : x))} className="w-full text-sm text-right border-0 focus:outline-none bg-transparent" />
                      </div>
                      <div className="px-3 py-2 text-right text-xs">
                        <div className="text-gray-700">{fmtMoney(cost)}</div>
                        <div className={`text-[11px] ${margin != null && margin >= 70 ? 'text-green-600' : margin != null && margin >= 60 ? 'text-amber-600' : 'text-red-600'}`}>{fmtPct(margin)} margin</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center gap-2">
                <button onClick={() => setParsed(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={saveAll} disabled={saving} className="flex-1 py-2 text-sm text-white rounded-lg font-medium disabled:opacity-50" style={{ background: 'var(--text-primary)', color: 'var(--bg-card)' }}>
                  {saving ? 'Saving…' : `Confirm & save ${parsed.length} items`}
                </button>
              </div>
              {error && <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-700">{error}</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Edit / add modal ──

// (Legacy EditItemModal removed — superseded by src/components/RecipeDrawer.js
// which hosts the new linkage state machine + margin attribution. See
// DESIGN_SYSTEM.md §Recipe Health surface for the architectural pattern.)
